<script>
  import { onMount } from "svelte";
  import {
    Page,
    Navbar,
    NavLeft,
    NavTitle,
    NavTitleLarge,
    NavRight,
    Link,
    Block,
    BlockTitle,
    Button,
    Icon,
    Card,
    CardContent,
    CardHeader,
    f7,
    f7ready,
  } from "framework7-svelte";
  import { fetchF1F2MigrationDocs } from "../js/navigationStore";
  import { SoundAlertManager } from "../utils/soundAlerts";

  export let f7route = undefined;
  export let f7router = undefined;

  // View Mode: 'dealer' (Dealer / Operator Mode) | 'tech' (Technical / Architecture Mode)
  let viewMode = "dealer";
  let selectedCategory = "all";
  let searchQuery = "";
  let loading = false;
  let copiedId = null;
  let activeFaq = null;

  // Initial resilient fallback data (mirrors backend SSOT catalog)
  let metadata = {
    title: "F1F2 to TRDR Autopilot Migration Guide",
    subtitle:
      "The Complete Operational & Architectural Blueprint: From 50-Tab Browser Chaos to 24/7 Cloud Autopilot",
    short_title: "F1F2 Autopilot Guide",
    version: "v3.0.0",
    last_updated: "2026-10-27",
    executive_summary:
      "Authoritative transition manual detailing the systemic transformation of TRDR's quantitative trading infrastructure. Eliminates 50 open browser tabs, single-client silos, manual lot math, and risky direct database edits with an autonomous compiled Go daemon, prime-stride fair rotational order queues, and a unified 1,000+ UCC dealer terminal.",
    total_transformations: 8,
    metrics: [
      {
        label: "Open Tabs Needed",
        value: "0 Tabs",
        subtext: "Down from 50+ memory-leaking browser tabs",
        badge: "ZERO TABS",
        badge_color: "blue",
      },
      {
        label: "Cloud Autopilot",
        value: "100%",
        subtext: "24/7 Go background daemon with auto-recovery",
        badge: "AUTOPILOT",
        badge_color: "green",
      },
      {
        label: "Concurrent UCC Capacity",
        value: "1,000+ UCCs",
        subtext: "Single command center replacing isolated silos",
        badge: "HIGH SCALE",
        badge_color: "purple",
      },
      {
        label: "Fanout Dispatch Latency",
        value: "< 50ms",
        subtext: "Prime-stride fair rotational queues eliminate delays",
        badge: "SUB-50MS",
        badge_color: "teal",
      },
    ],
  };

  let transformations = [
    {
      id: "tab-nightmare-vs-cloud-autopilot",
      number: 1,
      title: "Tab Nightmare vs. Cloud Autopilot",
      tagline: "From 50 open browser tabs with setInterval to 24/7 autonomous background Go daemon",
      category: "System Reliability & Runtime",
      f1f2_before: {
        headline: "50 Open Chrome Tabs & 'Please Do Not Close Until 03:35 PM'",
        description:
          "Every client required a dedicated browser tab running client-side JavaScript timers. If an operator accidentally closed a tab, if Chrome crashed from out-of-memory, or if the laptop went to sleep on low battery, all automated trading for that client froze instantly with zero alert.",
        pain_points: [
          "Laptops overheated running 50+ Chrome processes chewing 16GB+ RAM",
          "Sticky notes on screens warning staff: 'DO NOT TOUCH OR CLOSE TABS!'",
          "Accidental Wi-Fi disconnects or laptop sleep killed market exit square-offs",
          "No process watchdog; crash of a single tab silently orphaned open positions",
        ],
        code_snippet: `// LEGACY F1F2 BROWSER-BASED CLIENT TIMER (Run in 50+ tabs)
console.log("Client Tab initialized for UCC: " + clientUCC);
alert("ATTENTION: Do not close or minimize this tab until 03:35 PM!");

setInterval(function() {
    var now = new Date();
    // Vulnerable: tab freezes when minimized or when laptop sleeps!
    if (now.getHours() === 15 && now.getMinutes() >= 25) {
        triggerAutoSquareOff(clientUCC);
    } else {
        pollSignalsForClient(clientUCC);
    }
}, 3000);`,
        risk_level: "CRITICAL",
        speed: "3,000ms polling jitter",
        icon: "f7:exclamationmark_triangle_fill",
      },
      trdr_now: {
        headline: "24/7 Autonomous Background Go Daemon (trdr-engine)",
        description:
          "All execution runs on high-availability Linux cloud servers inside trdr-engine. Operates completely independently of any dealer's laptop or browser. Dealers can turn off their machines, disconnect, or commute home while the Go engine executes signals and risk rules autonomously.",
        key_benefits: [
          "Zero open tabs required; browser is strictly a real-time monitor, not the runtime",
          "Sub-second market session state machine (Pre-Open, Normal, Square-Off, Closed)",
          "Systemd daemon auto-restart with state persistence in MariaDB and in-memory caches",
          "Complete immunity to client workstation crashes, power cuts, or browser hangs",
        ],
        code_snippet: `// MODERN TRDR CLOUD DAEMON (trdr-engine/cmd/engine/main.go)
func main() {
    ctx, cancel := context.WithCancel(context.Background())
    defer cancel()

    log.Info().Msg("Starting TRDR Cloud Engine 24/7 Autopilot...")
    fanout := strategy.NewFanoutEngine(bseGateway, store, baseLotSize)
    sessionMgr := session.NewMarketSessionManager(marketCalendar)

    // Autonomous event-driven market loop running independent of dealer UI
    go sessionMgr.WatchSessionTransitions(ctx, func(event session.Event) {
        if event.Type == session.AutoSquareOffTriggered {
            fanout.ExecuteGlobalSquareOff(ctx)
        }
    })
}`,
        architecture_specs: [
          "Go runtime with goroutine scheduler and zero GC latency spikes",
          "High-availability systemd service with automatic watchdog ping",
          "Stateless browser WebSocket connection; disconnecting does not stop orders",
        ],
        action_route: "/dealer-terminal/",
        action_label: "Open Live Dealer Terminal",
        risk_level: "ZERO-RISK",
        speed: "< 5ms runtime response",
        icon: "f7:bolt_fill",
      },
      dealer_explanation:
        "In F1F2, you had to keep dozens of browser tabs open all day, praying your laptop wouldn't freeze or restart. In TRDR, the trading robot runs 24/7 in our secure cloud servers. You can safely close your laptop, shut down your browser, or lose internet connection—your trades and square-offs continue running safely.",
      tech_explanation:
        "Decoupled execution runtime from browser frontend. trdr-engine executes as an asynchronous compiled Go binary with BSE BOW gateway TCP sockets. The frontend communicates via push WebSockets solely for UI rendering; disconnects do not interrupt active trading sessions or order routing.",
    },
    {
      id: "single-client-silos-vs-unified-dealer-terminal",
      number: 2,
      title: "Single-Client Silos vs. Unified Dealer Terminal",
      tagline: "From 1 tab per client to a centralized command center managing 1,000+ UCCs",
      category: "Operational Command & Scale",
      f1f2_before: {
        headline: "1 Browser Tab Per Client Silo with Isolated Logins",
        description:
          "Managing 50 clients required 50 individual browser windows. Dealers had to click through each tab sequentially to view account balances, verify positions, or manually intervene during volatile market movements. No aggregated risk view existed.",
        pain_points: [
          "Dealer mental fatigue from Alt-Tabbing across dozens of identical-looking windows",
          "Impossible to know company-wide total net exposure across all client accounts",
          "During market crashes, emergency panic square-off had to be clicked tab by tab",
          "Orphaned tabs frequently traded wrong lot sizes because wrong client tab was active",
        ],
        code_snippet: `// LEGACY F1F2 MULTI-WINDOW LAUNCHER (launcher.js)
var clientList = ["UCC001", "UCC002", "UCC003", /* ... 50 clients */];
function openAllClientTabs() {
    clientList.forEach(function(ucc) {
        // Pops up 50 separate browser windows
        window.open("client_trade_view.php?ucc=" + ucc, "_blank");
    });
}`,
        risk_level: "HIGH",
        speed: "15+ minutes to inspect all clients",
        icon: "f7:square_stack_3d_down_right_fill",
      },
      trdr_now: {
        headline: "Centralized Command Center (/dealer-terminal/) for 1,000+ UCCs",
        description:
          "Single high-density dealer console displays every active client account, real-time MTM P&L, margin utilization, and connection state in one unified grid. Includes instant search, multi-client bulk actions, and emergency global kill switches.",
        key_benefits: [
          "Real-time consolidated company-wide net position and gross margin exposure",
          "1-Click Global Emergency Square-Off across all 1,000+ client accounts simultaneously",
          "Client drawer modal allows instant deep-dive without leaving the command center",
          "Visual alert badges for margin calls, connectivity loss, or rejected orders",
        ],
        code_snippet: `// TRDR UNIFIED DEALER TERMINAL (src/pages/dealer-terminal.svelte)
import { MultiClientOrderGrid, GlobalKillSwitch } from "../components/dealer";

// Single reactive subscription streams all 1,000+ client UCCs simultaneously
$: totalExposure = $activePositions.reduce((acc, p) => acc + p.netAmount, 0);
$: totalRealizedPnL = $activeClients.reduce((acc, c) => acc + c.realizedPnL, 0);

// Unified UI rendering:
// <DealerCommandHeader {totalExposure} {totalRealizedPnL} />
// <GlobalKillSwitch on:emergencyStop={handleEmergencySquareOff} />
// <MultiClientOrderGrid clients={$activeClients} on:selectClient={openDrawer} />`,
        architecture_specs: [
          "Virtual scrolling DOM handles 10,000+ rows at 60 FPS without memory bloat",
          "Selective WebSocket patch diffing prevents unnecessary component re-renders",
          "Role-based permission gating ensures dealers only control authorized client groups",
        ],
        action_route: "/dealer-terminal/",
        action_label: "Launch Dealer Terminal",
        risk_level: "ZERO-RISK",
        speed: "< 10ms UI filter response",
        icon: "f7:gauge_badge_plus",
      },
      dealer_explanation:
        "Instead of juggling 50 different browser windows, you now have one sleek trading screen. You can see all your clients, their total profit/loss, and open orders on a single page, with one big red Emergency Stop button that safely closes all positions in seconds.",
      tech_explanation:
        "Engineered a high-performance reactive Svelte state store backed by binary WebSocket frames from trdr-engine. Aggregated multi-tenant metrics are pre-computed in Go memory and broadcast as atomic diffs, maintaining 60 FPS rendering regardless of client count.",
    },
    {
      id: "order-delay-vs-fair-rotational-fanout",
      number: 3,
      title: "Order Delay vs. Fair Rotational Fanout",
      tagline: "From slow tab-by-tab sequential execution to concurrent millisecond dispatch via prime-stride fair queues",
      category: "Execution Speed & Fairness",
      f1f2_before: {
        headline: "Tab-by-Tab Slow Sequential Execution & Client Starvation",
        description:
          "When an algorithmic signal fired, legacy scripts iterated through clients one-by-one synchronously over HTTP. The first client in the database got placed at 09:15:01, but client #50 didn't receive their order until 09:16:30, suffering massive slippage and unfair order fills.",
        pain_points: [
          "Client #50 consistently got worse fills, higher slippage, or completely missed entry prices",
          "If client #12 suffered a network timeout, the entire execution loop hung for 30 seconds",
          "Regulatory fairness audit risk: favoritism towards clients sorted early in alphabetical order",
          "No asynchronous concurrency; execution time scaled linearly with client count (O(N))",
        ],
        code_snippet: `// LEGACY F1F2 SEQUENTIAL LOOP (trade_dispatcher.php)
$clients = mysqli_query($db, "SELECT ucc, lot_size FROM clients ORDER BY ucc ASC");
foreach ($clients as $client) {
    // Blocking synchronous HTTP request to broker gateway
    $response = sendBrokerOrder($client['ucc'], $signalSymbol, $client['lot_size']);
    // Client #50 waits until 49 previous HTTP round-trips finish!
    sleep(1); 
}`,
        risk_level: "CRITICAL",
        speed: "45,000ms - 90,000ms total loop latency",
        icon: "f7:timer",
      },
      trdr_now: {
        headline: "Concurrent Millisecond Dispatch via Prime-Stride Fair Queues (fanout.go)",
        description:
          "TRDR dispatches child orders in parallel goroutines within milliseconds. Furthermore, rotational prime-stride permutation ensures no single client always trades first, guaranteeing mathematical fairness and regulatory compliance across all executions.",
        key_benefits: [
          "Sub-50ms total fanout time across all 1,000+ accounts concurrently",
          "Prime-stride fair rotational ordering eliminates execution bias across market days",
          "Independent timeout barriers: a slow broker socket for one client never blocks others",
          "Deterministic audit trail logging order dispatch timestamp down to microsecond precision",
        ],
        code_snippet: `// MODERN TRDR PRIME-STRIDE FANOUT ENGINE (trdr-engine/internal/strategy/fanout.go)
func (f *FanoutEngine) executeFanout(ctx context.Context, subs []ClientSubscription, sig *SignalEvent, gw gateway.Gateway, isSim bool) ([]*bse_bow.OrderAck, error) {
    var wg sync.WaitGroup
    resultsChan := make(chan *bse_bow.OrderAck, len(subs))

    // Prime-stride rotational shuffling prevents execution bias
    stride := getCoprimeStride(len(subs))
    for i := range subs {
        idx := (i * stride) % len(subs)
        sub := subs[idx]
        
        wg.Add(1)
        go func(clientSub ClientSubscription) {
            defer wg.Done()
            // Non-blocking concurrent dispatch to BSE BOW socket
            ack, err := f.dispatchChildOrder(ctx, clientSub, sig, gw, isSim)
            if err == nil { resultsChan <- ack }
        }(sub)
    }
    wg.Wait()
    return collectAcks(resultsChan), nil
}`,
        architecture_specs: [
          "Prime stride coprime permutation formula ensures uniform distribution across runs",
          "Bounded worker pool with sync.WaitGroup prevents CPU starvation under spike volume",
          "Non-blocking channel fan-in collects broker acks in < 50ms total window",
        ],
        action_route: "/dealer-terminal/",
        action_label: "Inspect Fanout Dispatch",
        risk_level: "ZERO-RISK",
        speed: "< 50ms concurrent fanout",
        icon: "f7:arrow_branch",
      },
      dealer_explanation:
        "In F1F2, orders were placed one-by-one. The first customer got a good price, but the 50th customer waited nearly two minutes and got a terrible price. In TRDR, orders for all 1,000 clients fire in parallel in a fraction of a second, and our rotation system guarantees fair prices for every single client.",
      tech_explanation:
        "Employs concurrent Go goroutines communicating through buffered channels. Rotation index is calculated via coprime stride: index = (i * stride) % N, where gcd(stride, N) == 1. This guarantees all subscribers are visited exactly once per signal wave with zero head-of-line blocking.",
    },
    {
      id: "manual-math-vs-dynamic-tier-and-investment-sizing",
      number: 4,
      title: "Manual Math vs. Dynamic Tier & Investment Sizing",
      tagline: "From calculating High/Medium/Low lots in head to in-memory CompanyStockRegistry with auto-calculated H/M/L tiers",
      category: "Risk Control & Allocation",
      f1f2_before: {
        headline: "Mental Math & Scribbled Notes for H/M/L Lot Sizing",
        description:
          "Operators had to memorize or check paper cheat sheets to calculate how many shares each client should trade based on High, Medium, or Low capital tiers. Fat-finger typos frequently resulted in 10x over-leveraging and catastrophic margin calls.",
        pain_points: [
          "Disastrous fat-finger errors: typing 5,000 shares instead of 500 into client forms",
          "Stale lot calculations ignored recent stock splits, bonus issues, or circuit limit changes",
          "High/Medium/Low risk definitions differed across operators with zero systematic consistency",
          "No automated check against client available trading margin before sending orders",
        ],
        code_snippet: `// LEGACY F1F2 MANUAL LOT CALCULATION (dealers_head_math.js)
// Operator had to manually remember:
// High Risk = 3 lots, Medium = 2 lots, Low = 1 lot
function getQuantity(stockPrice, clientRiskLevel) {
    if (clientRiskLevel === "HIGH") return 300; // Hardcoded, hope price didn't double!
    if (clientRiskLevel === "MED") return 200;
    return 100; // Frequent margin shortfall errors!
}`,
        risk_level: "HIGH",
        speed: "Manual calculation (30-60s per client)",
        icon: "f7:multiply_circle_fill",
      },
      trdr_now: {
        headline: "In-Memory CompanyStockRegistry with Auto-Calculated H/M/L Tiers",
        description:
          "TRDR's in-memory CompanyStockRegistry continuously maintains validated High, Medium, and Low lot tiers for every scrip, dynamically scaling child orders based on the client's configured risk multiplier and exact available cash balance.",
        key_benefits: [
          "Dynamic H/M/L tier calculation tied to scrip price, volatility, and exchange circuit filters",
          "Pre-trade MultiClientRMS margin validation blocks orders exceeding client cash limits",
          "Instant propagation: modifying a scrip's base lot size updates all 1,000 clients in 0ms",
          "Fat-finger immunity with hard ceiling limits per trade, per scrip, and per portfolio",
        ],
        code_snippet: `// MODERN TRDR IN-MEMORY REGISTRY (trdr-engine/internal/strategy/company_registry.go)
type CompanyStockDef struct {
    Symbol       string  \`json:"symbol"\`
    BSEToken     int     \`json:"bse_token"\`
    TradingSt    int     \`json:"trading_st"\` // 1 = Active, 0 = Blocked
    BaseLot      int     \`json:"base_lot"\`
    HighTierLot  int     \`json:"high_tier_lot"\`
    MedTierLot   int     \`json:"med_tier_lot"\`
    LowTierLot   int     \`json:"low_tier_lot"\`
}

func (r *CompanyStockRegistry) ResolveClientQty(symbol string, sub ClientSubscription) int {
    def, ok := r.GetBySymbol(symbol)
    if !ok || def.TradingSt == 0 { return 0 } // Auto-drop inactive scrips
    
    tierBase := def.GetTierLot(sub.RiskTier) // High, Med, or Low
    return int(float64(tierBase) * sub.RiskMultiplier)
}`,
        architecture_specs: [
          "Concurrent lock-free atomic pointer reads for nanosecond symbol resolution",
          "Bi-directional indexing by both NSE Symbol and BSE Security Token",
          "Syncs with MariaDB master tables on startup with zero-downtime hot reload",
        ],
        action_route: "/master-data-manager/",
        action_label: "Configure Scrip Lot Tiers",
        risk_level: "ZERO-RISK",
        speed: "< 1 microsecond memory lookup",
        icon: "f7:equal_square_fill",
      },
      dealer_explanation:
        "You never have to calculate lot sizes in your head again. TRDR automatically calculates the exact safe quantity for High, Medium, and Low risk clients based on real-time stock prices. If a client doesn't have enough margin, the system safely adjusts or skips the order before it can trigger an exchange penalty.",
      tech_explanation:
        "The CompanyStockRegistry maintains a thread-safe in-memory cache of all exchange scrip definitions. Child order quantities are computed deterministically by the MultiClientRMS pre-trade risk engine before any socket dispatch, completely eliminating margin shortfall rejections from BSE BOW.",
    },
    {
      id: "fragmented-scripts-vs-central-strategy-manager",
      number: 5,
      title: "Fragmented Scripts vs. Central Strategy Manager",
      tagline: "From scattered PHP scripts (S3, S4, S5, Gap Breakout) to centralized UI with 1-click kill switches and risk multipliers",
      category: "Strategy Governance & Risk",
      f1f2_before: {
        headline: "Scattered PHP Scripts Across Cron Jobs with Hardcoded Logic",
        description:
          "Strategies (S3, S4, S5, Gap Breakout) were loose PHP files sitting in server directories. Changing a stop-loss or turning off a malfunctioning strategy required SSH-ing into the server and editing raw PHP code with nano in production during market hours.",
        pain_points: [
          "Zero visibility: impossible to know which strategies were currently active or trading",
          "Disastrous syntax errors: editing live PHP files during market volatility could break all trading",
          "No kill switch: stopping a runaway strategy required hunting down and killing Linux PID processes",
          "No audit trail or versioning: anyone with server access could silently change parameters",
        ],
        code_snippet: `// LEGACY F1F2 SCATTERED SCRIPT (S3_strategy_cron.php)
// To stop S3, engineer had to comment out code on production server!
// if ($market_drop) { exit("Strategy stopped manually"); }

require("db_connect.php");
$stop_loss_pct = 0.015; // Hardcoded in file! Needed vim to change!
$query = "SELECT * FROM signals WHERE strategy='S3' AND status='PENDING'";
// Execute unmonitored cron job...`,
        risk_level: "CRITICAL",
        speed: "10-20 minutes to SSH and modify files",
        icon: "f7:doc_plaintext",
      },
      trdr_now: {
        headline: "Centralized Strategy & Risk Manager (/strategy-manager/)",
        description:
          "Dedicated web management console gives complete control over every strategy (S3, S4, S5, Gap Breakout, AMC Momentum). Features instant master kill switches, dynamic risk multipliers, allocated client counters, and real-time execution statistics.",
        key_benefits: [
          "1-Click instant toggle switch to pause, activate, or kill any strategy in real-time",
          "Dynamic risk multiplier slider (0.5x to 3.0x) adjusts position sizing on the fly",
          "Live signal feed shows real-time buy/sell triggers with symbol, entry price, and target",
          "Full audit logging tracks who changed strategy parameters and when",
        ],
        code_snippet: `// MODERN TRDR STRATEGY MANAGER (src/pages/strategy-manager.svelte)
<StrategyCard 
  key="S3_INTRADAY"
  name="S3 Intraday Momentum"
  active={strat.isActive}
  riskMultiplier={strat.multiplier}
  activeSubscribers={strat.subscriberCount}
  on:toggle={(e) => updateStrategyStatus('S3_INTRADAY', e.detail.active)}
  on:killSwitch={() => emergencyKillStrategy('S3_INTRADAY')}
/>`,
        architecture_specs: [
          "Instant zero-restart config synchronization via atomic engine control channels",
          "Sub-second WebSocket broadcast reflects strategy state changes to all logged-in dealers",
          "Pre-flight authorization guards restrict kill switches to authorized org administrators",
        ],
        action_route: "/strategy-manager/",
        action_label: "Open Strategy Manager",
        risk_level: "ZERO-RISK",
        speed: "Instant (< 50ms toggle response)",
        icon: "f7:slider_horizontal_3",
      },
      dealer_explanation:
        "In F1F2, if a strategy went wrong, a developer had to urgently log into the server and edit code files while money was on the line. In TRDR, you have a clean Strategy Manager dashboard. If market volatility spikes, you simply click one button to pause the strategy or adjust its risk with a slider.",
      tech_explanation:
        "Architected an event-driven control plane where strategy state mutations from the frontend pass through GraphQL mutations into the Go engine's control channel. The engine dynamically activates or disables subscriber channels without restarting the binary or dropping active socket connections.",
    },
    {
      id: "direct-db-edits-vs-modern-master-data-manager",
      number: 6,
      title: "Direct DB Edits vs. Modern Master Data Manager",
      tagline: "From manual phpMyAdmin edits to f1f2_company_array to validated UI with scrip search and instant sync",
      category: "Master Data Integrity",
      f1f2_before: {
        headline: "Editing f1f2_company_array in phpMyAdmin with Zero Validation",
        description:
          "Adding or removing stocks required logging into phpMyAdmin and editing serialized PHP arrays or JSON strings directly in the database. A single missing quotation mark, wrong scrip token, or syntax typo corrupted the entire company list and crashed the trading engine.",
        pain_points: [
          "Catastrophic crashes when operators made typos in raw SQL or phpMyAdmin grids",
          "No validation against exchange scrip lists; invalid symbols rejected at market open",
          "Changes required manually restarting all 50 browser tabs to reload the updated array",
          "No historical revision history: impossible to revert accidental deletions of company masters",
        ],
        code_snippet: `// LEGACY F1F2 DIRECT DATABASE MANIPULATION (phpMyAdmin SQL dump)
-- Danger: 1 wrong character broke the entire trading engine!
UPDATE f1f2_settings 
SET f1f2_company_array = 'a:3:{i:0;s:8:"RELIANCE";i:1;s:4:"TCS";i:2;s:8:"INFY";}'
WHERE id = 1;
-- If serialization length was wrong, unserialize() returned FALSE and stopped all orders!`,
        risk_level: "CRITICAL",
        speed: "15 minutes of risky database hacking",
        icon: "f7:cylinder_split",
      },
      trdr_now: {
        headline: "Modern Master Data Manager UI (/master-data-manager/) with Instant Sync",
        description:
          "Intuitive web interface with autocomplete scrip search, real-time exchange security token verification, automatic tick size lookup, and 1-click active/inactive toggles. Updates sync instantly to engine memory without requiring server restarts.",
        key_benefits: [
          "Strict input validation: exchange security tokens and tick sizes verified automatically",
          "Instant hot-reload: engine picks up new scrips within 50ms without stopping trading",
          "Search and filter by exchange (NSE/BSE), series (EQ/BE), and active trading status",
          "Safe deactivation: toggle a stock OFF with one click to instantly drop all incoming signals",
        ],
        code_snippet: `// MODERN TRDR MASTER DATA API (trdr-backend/trdr-admin-services/MAPPGQL.ts)
async update_scrip_master(args: { symbol: string, bseToken: number, active: boolean }) {
    // 1. Strict schema validation
    validateExchangeToken(args.bseToken);
    
    // 2. Atomic database update
    await ScripMaster.updateOne({ symbol: args.symbol }, { $set: args }, { upsert: true });
    
    // 3. Hot-reload notify engine memory cache
    await notifyEngineMasterDataSync(args.symbol);
    return { status: true, message: "Scrip synced to engine memory" };
}`,
        architecture_specs: [
          "Exchange master sync against official daily bhavcopy and scrip contract files",
          "In-memory atomic pointer swap in Go engine ensures zero-downtime scrip updates",
          "Full audit logging recording operator user ID, IP address, and timestamp for all edits",
        ],
        action_route: "/master-data-manager/",
        action_label: "Open Master Data Manager",
        risk_level: "ZERO-RISK",
        speed: "Instant (< 50ms sync)",
        icon: "f7:folder_fill",
      },
      dealer_explanation:
        "In F1F2, adding a new stock meant editing confusing database tables where one typo could break everything. In TRDR, you simply open Master Data Manager, search for the stock symbol, verify the exchange code, and click Save. The trading engine updates immediately.",
      tech_explanation:
        "Eliminated serialized database blobs in favor of normalized relational schemas and GraphQL mutation endpoints with strict type validation. Engine subscribes to database notification triggers and hot-reloads the CompanyStockRegistry in memory using sync.RWMutex.",
    },
    {
      id: "risky-live-testing-vs-isolated-paper-trading-sandbox",
      number: 7,
      title: "Risky Live Testing vs. Isolated Paper-Trading Sandbox",
      tagline: "From testing risking real money to hard-isolated LIVE vs SIMULATION toggle with cryptographic safety barriers",
      category: "Sandbox Isolation & Compliance",
      f1f2_before: {
        headline: "Testing on Live Production Accounts with Real Capital at Risk",
        description:
          "F1F2 had no simulation environment. Developers tested new algorithms or gateway bugfixes on actual live broker accounts with real money, manually trying to cancel orders or setting lot sizes to 1, frequently losing real capital due to test bugs.",
        pain_points: [
          "Real customer money lost because developers commented out wrong lines of code",
          "Accidental live order dispatch when intending to test local changes",
          "No sandbox order book simulator to test exchange rejects, circuit hits, or partial fills",
          "Regulatory violation: SEBI mandates strict isolation between live and test trading harnesses",
        ],
        code_snippet: `// LEGACY F1F2 DANGEROUS TEST COMMENTING (test_broker.php)
// DANGER: If developer forgot to comment this out, real money was traded!
// $is_testing = true;
// if (!$is_testing) {
    placeRealBSEOrder($liveAccount, $symbol, $qty); // Real order placed by mistake!
// }`,
        risk_level: "CRITICAL",
        speed: "High financial risk on every test",
        icon: "f7:exclamationmark_octagon_fill",
      },
      trdr_now: {
        headline: "Hard-Isolated LIVE vs. SIMULATION Toggle with Gateway Barriers",
        description:
          "TRDR features a cryptographically isolated paper-trading sandbox. Live and simulation environments use completely separate gateway drivers (f.gateway vs f.simGateway). Simulated accounts cannot route orders to live exchange sockets under any circumstance.",
        key_benefits: [
          "Physical gateway isolation: simulation routines cannot reach live exchange TCP sockets",
          "Full paper-trading simulator models realistic exchange order acks, fills, and rejections",
          "Test new strategies and train dealers with live market tick data without risking 1 rupee",
          "Prominent visual sandbox banner and color coding in UI prevents operator confusion",
        ],
        code_snippet: `// MODERN TRDR GATEWAY ISOLATION (trdr-engine/internal/strategy/fanout.go)
func (f *FanoutEngine) DispatchSimSignal(ctx context.Context, sig *SignalEvent) ([]*bse_bow.OrderAck, error) {
    subs := f.getSimSubscribers(sig.StrategyKey, sig.ResolveTargetClient())
    gw := f.simGateway
    if gw == nil {
        // Cryptographic safety barrier: refuse to route simulation to live gateway!
        return nil, errors.New("fanout: simulation gateway not configured; refusing to route to live gateway")
    }
    return f.executeFanout(ctx, subs, sig, gw, true)
}`,
        architecture_specs: [
          "Dedicated virtual exchange socket emulator implementing BSE BOW binary contract",
          "Independent simulation database table prevents test records from polluting accounting audits",
          "One-click environment switch with cryptographic token verification",
        ],
        action_route: "/dealer-terminal/",
        action_label: "View Simulation Sandbox",
        risk_level: "ZERO-RISK",
        speed: "Instant zero-risk execution",
        icon: "f7:checkmark_shield_fill",
      },
      dealer_explanation:
        "You can now test new strategies or train new operators in complete safety. TRDR has a dedicated Simulation Mode with virtual funds that behaves exactly like the real market, but with guaranteed zero risk to real capital. A built-in security barrier makes it physically impossible for test orders to touch real broker accounts.",
      tech_explanation:
        "Enforced strict architectural separation at the interface boundary. FanoutEngine maintains distinct subscription maps (liveSubs and simSubs) and distinct gateway instances. Even under severe bug scenarios, simulation signals are structurally prohibited from accessing the live BSE BOW gateway pointer.",
    },
    {
      id: "static-html-tables-vs-live-streams-and-sound-alerts",
      number: 8,
      title: "Static HTML Tables vs. Live Streams & Sound Alerts",
      tagline: "From manual page refreshes to live WebSockets, interactive scrip charts, and audio chimes",
      category: "Terminal Experience & Real-Time Awareness",
      f1f2_before: {
        headline: "Static HTML Tables Requiring Constant F5 Browser Refreshes",
        description:
          "F1F2 rendered plain server-side HTML tables. To check if an order was filled or rejected, dealers had to spam F5 to refresh the page. Rejected orders failed silently with no auditory or visual cue, leaving dealers unaware until market close.",
        pain_points: [
          "Dealers constantly pressing F5, increasing server load and causing page timeouts",
          "Silent rejections: an order rejected for margin shortfall sat unnoticed for hours",
          "No audio feedback: dealers had to stare unblinkingly at numbers all trading session",
          "Zero charting: dealers had to open third-party trading apps just to see a basic price chart",
        ],
        code_snippet: `<!-- LEGACY F1F2 STATIC HTML (order_list.php) -->
<!-- Required operator to constantly refresh browser -->
<meta http-equiv="refresh" content="30"> <!-- 30 second blind spot! -->
<table>
    <tr><th>Client</th><th>Order</th><th>Status</th></tr>
    <?php while($row = mysqli_fetch_assoc($res)) { ?>
        <tr><td><?= $row['ucc'] ?></td><td><?= $row['symbol'] ?></td><td><?= $row['status'] ?></td></tr>
    <?php } ?>
</table>`,
        risk_level: "MEDIUM",
        speed: "30-second stale data refresh",
        icon: "f7:arrow_2_circlepath",
      },
      trdr_now: {
        headline: "Real-Time WebSockets, Interactive Charts & Synthesized Sound Alerts (soundAlerts.js)",
        description:
          "TRDR streams live tick data, order fills, and position updates directly over WebSockets with zero page refreshes. Features built-in candlestick charts and zero-asset synthesized Web Audio chimes for immediate situational awareness.",
        key_benefits: [
          "Real-time WebSocket streaming pushes order fills, cancellations, and rejections instantly",
          "Zero-asset synthesized Web Audio chimes (soundAlerts.js) provide distinct audio cues",
          "Interactive embedded charts show real-time scrip price action and order execution markers",
          "Instant visual toast notifications with color-coded risk and fill badges",
        ],
        code_snippet: `// MODERN TRDR AUDIO SYNTHESIZER (src/utils/soundAlerts.js)
export const SoundAlertManager = {
    playChime(type = 'ORDER_FILLED') {
        const ctx = getAudioContext();
        if (!ctx) return;
        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        if (type === 'ORDER_FILLED') {
            // High harmonic double-beep for successful fills
            osc.frequency.setValueAtTime(880, now);
            osc.frequency.setValueAtTime(1320, now + 0.08);
            gain.gain.setValueAtTime(0.3, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
            osc.start(now);
            osc.stop(now + 0.25);
        }
    }
};`,
        architecture_specs: [
          "Web Audio API oscillator synthesis requires zero external MP3/WAV asset downloads",
          "WebSocket connection multiplexes tick streams, order books, and notifications in one socket",
          "Canvas-accelerated lightweight charting renders high-frequency ticks smoothly",
        ],
        action_route: "/dealer-terminal/",
        action_label: "Experience Live Streaming",
        risk_level: "ZERO-RISK",
        speed: "< 5ms instant audio-visual feedback",
        icon: "f7:speaker_wave_2_fill",
      },
      dealer_explanation:
        "You never have to hit refresh again. The terminal updates automatically the microsecond an order is placed. When an order fills, you hear a pleasant chime; if an order is rejected, you hear an alert and see an immediate warning toast. You can keep the terminal in the background and know immediately what happens.",
      tech_explanation:
        "Leveraged WebSockets for reactive pub/sub event distribution paired with client-side Web Audio API frequency synthesis. Eliminates network asset dependencies for audio cues while ensuring zero-latency trader notification upon broker order acknowledgments.",
    },
  ];

  let faqs = [
    {
      id: "faq-laptop-battery",
      question: "What happens if my laptop battery dies or my Wi-Fi disconnects during market hours?",
      answer:
        "Nothing bad happens! In TRDR, the trading robot runs 24/7 on our cloud servers (trdr-engine), not on your computer. Your open trades, trailing stop-losses, and automated 03:25 PM square-offs will execute completely normally. When you reconnect or open your laptop, the terminal will instantly sync with the current live state.",
      category: "Operations",
      role_hint: "Dealer / Operator",
    },
    {
      id: "faq-s3-s4-signals",
      question: "Where did my legacy S3, S4, and Gap Breakout signals go?",
      answer:
        "All legacy strategies have been migrated into the unified Strategy & Risk Manager (/strategy-manager/). You no longer have to check separate PHP scripts or cron job logs. You can see active signals, adjust risk multipliers, or pause any strategy with a single click from the dashboard.",
      category: "Execution",
      role_hint: "Dealer / Operator",
    },
    {
      id: "faq-change-lot-sizing",
      question: "How do I change client lot sizing or High/Medium/Low tiers?",
      answer:
        "You no longer calculate lots in your head. Open Master Data Manager (/master-data-manager/) to configure the base lot size and tier allocations for any scrip. The system automatically calculates the exact share quantity for every client based on their configured risk tier and available margin.",
      category: "Risk & Safety",
      role_hint: "Dealer / Operator",
    },
    {
      id: "faq-fair-rotational-fanout",
      question: "Why do my clients get different order sequence each time?",
      answer:
        "This is TRDR's Prime-Stride Fair Rotational Fanout in action. In the old F1F2 system, clients sorted at the beginning of the alphabet always got executed first, while clients at the end suffered bad slippage. TRDR rotates the dispatch order fairly on every single signal wave so all clients receive mathematically equal execution quality.",
      category: "Execution",
      role_hint: "Technical / Compliance",
    },
    {
      id: "faq-emergency-kill-switch",
      question: "How do I trigger an emergency stop if extreme market volatility occurs?",
      answer:
        "In the Dealer Terminal (/dealer-terminal/), click the prominent red 'Global Kill Switch' button at the top of the screen. This immediately cancels all pending orders and initiates fair parallel square-offs across all accounts in under 50 milliseconds.",
      category: "Risk & Safety",
      role_hint: "Dealer / Operator",
    },
    {
      id: "faq-paper-trading-verification",
      question: "Can I verify a strategy without risking real client money?",
      answer:
        "Yes. Simply toggle the client or strategy to 'SIMULATION' mode. The orders will route strictly to our simulated virtual exchange gateway, processing live tick data with zero financial exposure. Cryptographic safeguards prevent test orders from ever reaching the live exchange.",
      category: "Risk & Safety",
      role_hint: "Dealer / Operator",
    },
  ];

  let codeComparison = {
    title: "The 50-Tab Trap vs. Cloud Autopilot Architecture",
    f1f2_code: `// ============================================================================
// LEGACY F1F2 ARCHITECTURE (Single-threaded Browser Tab Nightmare)
// File: client_tab_watcher.js - Placed in 50 open Chrome tabs
// ============================================================================

var clientUCC = "UCC_78912";
var intervalSeconds = 3;

// Dealer warning sticky note: DO NOT CLOSE OR REFRESH TAB!
document.title = "DO NOT CLOSE - ACTIVE CLIENT: " + clientUCC;
alert("Keep this tab open until 03:35 PM or trading will FAIL!");

setInterval(function() {
    var now = new Date();
    // Vulnerable: tab freezes when minimized or when laptop sleeps!
    if (now.getHours() === 15 && now.getMinutes() >= 25) {
        // Tab-by-tab slow square-off
        $.post("/legacy/square_off.php", { ucc: clientUCC }, function(res) {
            console.log("Square off response: " + res);
        });
    } else {
        // Polls PHP backend synchronously
        $.get("/legacy/get_signals.php?ucc=" + clientUCC, function(data) {
            if (data.signal) {
                // Sequential placement with massive slippage
                $.post("/legacy/place_order.php", {
                    ucc: clientUCC,
                    symbol: data.symbol,
                    qty: 500 // Hardcoded mental math
                });
            }
        });
    }
}, intervalSeconds * 1000);`,
    f1f2_caption: "Legacy F1F2: 50 open browser tabs running client-side setInterval, vulnerable to laptop sleep, memory leaks, and browser crashes.",
    trdr_code: `// ============================================================================
// MODERN TRDR ARCHITECTURE (24/7 Cloud Autopilot in Compiled Go)
// File: trdr-engine/internal/strategy/fanout.go - High Availability Daemon
// ============================================================================

package strategy

import (
    "context"
    "sync"
    "time"
    "trdr-engine/internal/gateway"
)

// Concurrent Fanout Engine: Zero open tabs needed
func (f *FanoutEngine) ExecuteFanout(ctx context.Context, subs []ClientSub, sig *SignalEvent) {
    var wg sync.WaitGroup
    stride := getCoprimeStride(len(subs)) // Fair rotational dispatch

    for i := range subs {
        idx := (i * stride) % len(subs)
        sub := subs[idx]

        wg.Add(1)
        go func(clientSub ClientSub) {
            defer wg.Done()
            // Dynamic tier lot calculation from in-memory registry
            qty := f.companyRegistry.ResolveClientQty(sig.Symbol, clientSub)
            if qty > 0 {
                // Concurrent dispatch to BSE BOW socket in <50ms
                f.gateway.SendOrder(ctx, clientSub.UCC, sig.Symbol, qty)
            }
        }(sub)
    }
    wg.Wait()
}`,
    trdr_caption: "Modern TRDR: High-concurrency Go background daemon executing prime-stride fair queues, sub-50ms dispatch, and automated risk controls.",
  };

  // Categories for filter pills
  const categories = [
    { id: "all", label: "All 8 Transformations" },
    { id: "System Reliability & Runtime", label: "Reliability & Daemon" },
    { id: "Operational Command & Scale", label: "Scale & Terminals" },
    { id: "Execution Speed & Fairness", label: "Execution & Fanout" },
    { id: "Risk Control & Allocation", label: "Risk & Lot Sizing" },
    { id: "Strategy Governance & Risk", label: "Strategy Manager" },
    { id: "Master Data Integrity", label: "Master Data & Scrips" },
    { id: "Sandbox Isolation & Compliance", label: "Simulation & Safety" },
    { id: "Terminal Experience & Real-Time Awareness", label: "WebSockets & Sound" },
  ];

  onMount(async () => {
    f7ready(async () => {
      loading = true;
      try {
        const data = await fetchF1F2MigrationDocs();
        if (data) {
          if (data.metadata) metadata = data.metadata;
          if (data.transformations && data.transformations.length > 0) {
            transformations = data.transformations;
          }
          if (data.faqs && data.faqs.length > 0) {
            faqs = data.faqs;
          }
          if (data.code_comparison) {
            codeComparison = data.code_comparison;
          }
        }
      } catch (err) {
        console.warn("Failed to fetch dynamic F1F2 docs from GraphQL, using SSOT cache", err);
      } finally {
        loading = false;
      }
    });
  });

  // Filter transformations
  $: filteredTransformations = transformations.filter((t) => {
    const matchesCategory = selectedCategory === "all" || t.category === selectedCategory;
    const q = searchQuery.toLowerCase().trim();
    if (!q) return matchesCategory;

    const matchesSearch =
      t.title.toLowerCase().includes(q) ||
      t.tagline.toLowerCase().includes(q) ||
      t.category.toLowerCase().includes(q) ||
      t.f1f2_before.headline.toLowerCase().includes(q) ||
      t.f1f2_before.description.toLowerCase().includes(q) ||
      t.trdr_now.headline.toLowerCase().includes(q) ||
      t.trdr_now.description.toLowerCase().includes(q) ||
      t.dealer_explanation.toLowerCase().includes(q) ||
      t.tech_explanation.toLowerCase().includes(q);

    return matchesCategory && matchesSearch;
  });

  function toggleFaq(id) {
    activeFaq = activeFaq === id ? null : id;
  }

  function printBriefing() {
    window.print();
  }

  function playSoundDemo(type) {
    try {
      SoundAlertManager.playChime(type);
      if (f7 && f7.toast) {
        f7.toast.create({
          text: `🔊 Chime Played: ${type}`,
          position: "bottom",
          closeTimeout: 2000,
        }).open();
      }
    } catch (e) {
      console.warn("Audio test failed", e);
    }
  }

  function copyCode(text, id) {
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      copiedId = id;
      setTimeout(() => {
        copiedId = null;
      }, 2000);
    }
  }

  function navigateTo(url) {
    if (!url) return;
    try {
      if (f7router && typeof f7router.navigate === "function") {
        f7router.navigate(url);
        return;
      }
      if (f7 && f7.views && f7.views.main && f7.views.main.router) {
        f7.views.main.router.navigate(url);
        return;
      }
      if (f7 && f7.view && f7.view.main && f7.view.main.router) {
        f7.view.main.router.navigate(url);
        return;
      }
    } catch (e) {
      console.warn("Navigation failed, falling back to window location", e);
    }
    window.location.href = url;
  }

  function goBack() {
    try {
      if (f7router && typeof f7router.back === "function") {
        f7router.back();
        return;
      }
      if (f7 && f7.views && f7.views.main && f7.views.main.router) {
        f7.views.main.router.back();
        return;
      }
    } catch (e) {
      console.warn("Router back failed", e);
    }
    window.history.back();
  }
</script>

<Page class="f1f2-guide-page">
  <!-- Top Navbar (Hidden on Print) -->
  <Navbar
    large
    sliding={false}
    style="--f7-navbar-bg-color: #0b0f19; --f7-navbar-text-color: #ffffff; --f7-navbar-link-color: #f97316;"
    class="no-print"
  >
    <NavLeft>
      <Link
        iconIos="f7:arrow_left"
        iconAurora="f7:arrow_left"
        iconMd="material:arrow_back"
        onClick={goBack}
      />
    </NavLeft>

    <NavTitle>F1F2 Autopilot Migration</NavTitle>
    <NavTitleLarge>F1F2 Autopilot Migration Guide</NavTitleLarge>

    <NavRight>
      <!-- Mode Switcher -->
      <div class="view-mode-toggle">
        <button
          class="mode-btn {viewMode === 'dealer' ? 'active' : ''}"
          on:click={() => (viewMode = 'dealer')}
          title="Dealer / Operator Mode: Plain non-technical English, operational FAQs, step-by-step guidance"
        >
          👔 Dealer Mode
        </button>
        <button
          class="mode-btn {viewMode === 'tech' ? 'active' : ''}"
          on:click={() => (viewMode = 'tech')}
          title="Technical Architecture Mode: Go specs, fanout routines, BSE BOW gateway contracts"
        >
          ⚙️ Architecture Mode
        </button>
      </div>

      <!-- Export / Print Briefing -->
      <Button
        fill
        small
        color="orange"
        onClick={printBriefing}
        style="margin-left: 10px; font-weight: 600;"
      >
        🖨️ Print PDF Briefing
      </Button>
    </NavRight>
  </Navbar>

  <!-- Print-Only Briefing Header -->
  <div class="print-header only-print">
    <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #0f172a; padding-bottom: 12px; margin-bottom: 16px;">
      <div>
        <h1 style="margin: 0; font-size: 22px; color: #0f172a; text-transform: uppercase; font-weight: 800;">
          TRDR Systems Architecture & Operational Evolution Briefing
        </h1>
        <p style="margin: 4px 0 0 0; font-size: 13px; color: #475569;">
          {metadata.subtitle}
        </p>
      </div>
      <div style="text-align: right; font-size: 11px; color: #64748b;">
        <div><strong>Version:</strong> {metadata.version} (SSOT)</div>
        <div><strong>Last Updated:</strong> {metadata.last_updated}</div>
        <div><strong>System Engine:</strong> trdr-engine (Go Daemon)</div>
      </div>
    </div>
  </div>

  <div class="page-content-wrapper">
    <!-- Executive Hero Card -->
    <div class="executive-hero-card">
      <div class="hero-top-row no-print">
        <div class="badge-group">
          <span class="badge-pill badge-autopilot">AUTOPILOT READY</span>
          <span class="badge-pill badge-version">{metadata.version} SSOT</span>
          <span class="badge-pill badge-contrast">0 TABS VS 50 TABS</span>
          <span class="badge-pill badge-engine">Compiled Go Daemon</span>
        </div>
        <div class="quick-nav-links">
          <Button
            small
            outline
            color="green"
            onClick={() => f7router.navigate("/dealer-terminal/")}
            style="border-radius: 6px; font-weight: 700; background: rgba(16, 185, 129, 0.1);"
          >
            ⚡ Live Terminal
          </Button>
          <Button
            small
            outline
            color="blue"
            onClick={() => f7router.navigate("/strategy-manager/")}
            style="border-radius: 6px; font-weight: 700; background: rgba(59, 130, 246, 0.1);"
          >
            📊 Strategy Manager
          </Button>
          <Button
            small
            outline
            color="orange"
            onClick={() => f7router.navigate("/master-data-manager/")}
            style="border-radius: 6px; font-weight: 700; background: rgba(249, 115, 22, 0.1);"
          >
            📁 Master Data
          </Button>
        </div>
      </div>

      <h1 class="hero-title">{metadata.title}</h1>
      <p class="hero-subtitle">{metadata.subtitle}</p>
      <p class="hero-desc">{metadata.executive_summary}</p>

      <!-- Big-Picture Metrics Grid -->
      <div class="metrics-grid">
        {#each metadata.metrics as metric}
          <div class="metric-card">
            <div class="metric-header">
              <span class="metric-badge {metric.badge_color}">{metric.badge}</span>
            </div>
            <div class="metric-value">{metric.value}</div>
            <div class="metric-label">{metric.label}</div>
            <div class="metric-subtext">{metric.subtext}</div>
          </div>
        {/each}
      </div>
    </div>

    <!-- Visual Contrast Section: The 50-Tab Trap vs Cloud Autopilot -->
    <div class="contrast-section">
      <div class="section-header">
        <div class="section-title-wrap">
          <span class="section-tag">ARCHITECTURAL SHOWCASE</span>
          <h2 class="section-title">{codeComparison.title}</h2>
          <p class="section-desc">
            Direct side-by-side contrast between the legacy F1F2 browser-based <code class="inline-code">setInterval</code> polling nightmare and TRDR's compiled cloud Go engine.
          </p>
        </div>
      </div>

      <div class="code-contrast-grid">
        <!-- Legacy F1F2 Code Card -->
        <div class="contrast-code-card legacy-card">
          <div class="code-card-header">
            <div class="code-title-left">
              <span class="danger-dot"></span>
              <span class="code-title-text">LEGACY F1F2: 50 Browser Tabs (PHP / JS)</span>
            </div>
            <span class="code-badge danger-badge">UNRELIABLE</span>
          </div>
          <div class="code-block-container">
            <pre class="code-pre"><code>{codeComparison.f1f2_code}</code></pre>
          </div>
          <div class="code-card-footer">
            <span class="warning-icon">⚠️</span>
            <span class="footer-caption">{codeComparison.f1f2_caption}</span>
          </div>
        </div>

        <!-- Modern TRDR Code Card -->
        <div class="contrast-code-card modern-card">
          <div class="code-card-header">
            <div class="code-title-left">
              <span class="success-dot"></span>
              <span class="code-title-text">MODERN TRDR: Cloud Autopilot (Compiled Go)</span>
            </div>
            <div style="display: flex; gap: 6px; align-items: center;">
              <span class="code-badge success-badge">SUB-50MS</span>
              <button
                class="copy-btn"
                on:click={() => copyCode(codeComparison.trdr_code, 'trdr-code')}
                title="Copy snippet"
              >
                {copiedId === 'trdr-code' ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          </div>
          <div class="code-block-container">
            <pre class="code-pre"><code>{codeComparison.trdr_code}</code></pre>
          </div>
          <div class="code-card-footer">
            <span class="success-icon">🚀</span>
            <span class="footer-caption">{codeComparison.trdr_caption}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Filter & Mode Control Bar (Hidden on Print) -->
    <div class="controls-bar no-print">
      <div class="category-pills">
        {#each categories as cat}
          <button
            class="cat-pill {selectedCategory === cat.id ? 'active' : ''}"
            on:click={() => (selectedCategory = cat.id)}
          >
            {cat.label}
          </button>
        {/each}
      </div>

      <div class="search-and-switch">
        <div class="search-box">
          <input
            type="text"
            placeholder="Search transformations, Go routines, scrips, or FAQs..."
            bind:value={searchQuery}
          />
          {#if searchQuery}
            <button class="clear-search" on:click={() => (searchQuery = '')}>✕</button>
          {/if}
        </div>

        <div class="view-indicator">
          Showing: <strong>{viewMode === 'dealer' ? '👔 Dealer / Operator Mode (Plain English)' : '⚙️ Architecture Mode (Go & Protocol Specs)'}</strong>
        </div>
      </div>
    </div>

    <!-- 8 Side-by-Side Comparison Cards -->
    <div class="transformations-container">
      {#if filteredTransformations.length === 0}
        <div class="empty-state">
          <h3>No matching transformations found</h3>
          <p>Try clearing your search query or choosing another category filter.</p>
          <Button outline small onClick={() => { selectedCategory = 'all'; searchQuery = ''; }}>
            Reset Filters
          </Button>
        </div>
      {/if}

      {#each filteredTransformations as item (item.id)}
        <div class="transformation-card" id={item.id}>
          <!-- Card Header Banner -->
          <div class="card-header-banner">
            <div class="header-left">
              <span class="trans-num">TRANSFORMATION #{item.number}</span>
              <span class="trans-cat">{item.category}</span>
            </div>
            <div class="header-right">
              {#if item.trdr_now.action_route}
                <Button
                  fill
                  small
                  color="orange"
                  onClick={() => f7router.navigate(item.trdr_now.action_route)}
                  style="font-weight: 700; border-radius: 6px;"
                >
                  🚀 {item.trdr_now.action_label} →
                </Button>
              {/if}
            </div>
          </div>

          <div class="card-content-body">
            <h3 class="trans-title">{item.title}</h3>
            <p class="trans-tagline">{item.tagline}</p>

            <!-- Mode-Specific Explanations -->
            {#if viewMode === 'dealer'}
              <div class="mode-box dealer-mode-box">
                <div class="mode-box-header">
                  <span class="mode-box-badge">👔 DEALER & OPERATOR BRIEFING</span>
                  <span class="mode-box-sub">Plain non-technical operational summary</span>
                </div>
                <p class="mode-box-text">{item.dealer_explanation}</p>
              </div>
            {:else}
              <div class="mode-box tech-mode-box">
                <div class="mode-box-header">
                  <span class="mode-box-badge tech-badge">⚙️ ARCHITECTURE & PROTOCOL SPEC</span>
                  <span class="mode-box-sub">trdr-engine daemon, socket protocols & MariaDB sync</span>
                </div>
                <p class="mode-box-text">{item.tech_explanation}</p>
              </div>
            {/if}

            <!-- Side-by-Side Comparison: F1F2 vs TRDR -->
            <div class="comparison-grid">
              <!-- Left: How it was done in F1F2 -->
              <div class="side-column legacy-column">
                <div class="column-header">
                  <div class="col-title-group">
                    <span class="status-pill pill-danger">LEGACY F1F2</span>
                    <span class="col-speed">{item.f1f2_before.speed}</span>
                  </div>
                  <span class="risk-badge risk-critical">{item.f1f2_before.risk_level}</span>
                </div>
                
                <h4 class="col-headline">{item.f1f2_before.headline}</h4>
                <p class="col-desc">{item.f1f2_before.description}</p>

                <div class="pain-points-list">
                  <div class="list-title">Operational Pain Points:</div>
                  <ul>
                    {#each item.f1f2_before.pain_points as pain}
                      <li>{pain}</li>
                    {/each}
                  </ul>
                </div>

                <!-- Code Snippet -->
                <div class="snippet-wrapper">
                  <div class="snippet-header">
                    <span>Legacy Script Sample</span>
                  </div>
                  <pre class="snippet-code"><code>{item.f1f2_before.code_snippet}</code></pre>
                </div>
              </div>

              <!-- Right: How it is done in TRDR -->
              <div class="side-column modern-column">
                <div class="column-header">
                  <div class="col-title-group">
                    <span class="status-pill pill-success">MODERN TRDR</span>
                    <span class="col-speed">{item.trdr_now.speed}</span>
                  </div>
                  <span class="risk-badge risk-zero">{item.trdr_now.risk_level}</span>
                </div>

                <h4 class="col-headline">{item.trdr_now.headline}</h4>
                <p class="col-desc">{item.trdr_now.description}</p>

                <div class="benefits-list">
                  <div class="list-title">System Improvements:</div>
                  <ul>
                    {#each item.trdr_now.key_benefits as benefit}
                      <li>{benefit}</li>
                    {/each}
                  </ul>
                </div>

                <!-- Code / Architecture Snippet -->
                <div class="snippet-wrapper modern-snippet">
                  <div class="snippet-header">
                    <span>Modern Architecture Contract</span>
                    <button
                      class="copy-btn-sm"
                      on:click={() => copyCode(item.trdr_now.code_snippet, item.id)}
                    >
                      {copiedId === item.id ? '✓' : 'Copy'}
                    </button>
                  </div>
                  <pre class="snippet-code"><code>{item.trdr_now.code_snippet}</code></pre>
                </div>

                {#if viewMode === 'tech' && item.trdr_now.architecture_specs}
                  <div class="tech-specs-bar">
                    <span class="specs-label">Specs:</span>
                    {#each item.trdr_now.architecture_specs as spec}
                      <span class="spec-tag">{spec}</span>
                    {/each}
                  </div>
                {/if}

                <!-- Direct Launch CTA -->
                <div class="col-action-footer no-print">
                  <Button
                    fill
                    small
                    color="green"
                    onClick={() => f7router.navigate(item.trdr_now.action_route)}
                    style="font-weight: 700; border-radius: 6px; display: inline-flex; align-items: center;"
                  >
                    Open in {item.trdr_now.action_label} →
                  </Button>

                  {#if item.id === 'static-html-tables-vs-live-streams-and-sound-alerts'}
                    <div class="sound-test-bar">
                      <span style="font-size: 11px; color: #94a3b8; font-weight: 600;">Test Chimes:</span>
                      <button class="sound-btn" on:click={() => playSoundDemo('ORDER_FILLED')}>
                        🔔 Order Filled
                      </button>
                      <button class="sound-btn" on:click={() => playSoundDemo('ORDER_REJECTED')}>
                        ⚠️ Rejected
                      </button>
                      <button class="sound-btn" on:click={() => playSoundDemo('KILLSWITCH')}>
                        🛑 Killswitch
                      </button>
                    </div>
                  {/if}
                </div>
              </div>
            </div>
          </div>
        </div>
      {/each}
    </div>

    <!-- Dealer FAQ & Cheatsheet Accordion -->
    <div class="faq-section">
      <div class="section-header">
        <div class="section-title-wrap">
          <span class="section-tag">DEALER CHEATSHEET & FAQS</span>
          <h2 class="section-title">Common Questions & Operational Safety</h2>
          <p class="section-desc">
            Essential FAQs for traders, operators, and compliance officers moving from F1F2 to TRDR.
          </p>
        </div>
      </div>

      <div class="faq-accordion-list">
        {#each faqs as faq}
          <div class="faq-item {activeFaq === faq.id ? 'active' : ''}">
            <button class="faq-question-btn" on:click={() => toggleFaq(faq.id)}>
              <div class="faq-q-left">
                <span class="faq-badge">{faq.category}</span>
                <span class="faq-question-text">{faq.question}</span>
              </div>
              <span class="faq-chevron">{activeFaq === faq.id ? '▲' : '▼'}</span>
            </button>
            {#if activeFaq === faq.id}
              <div class="faq-answer-panel">
                <div class="faq-role-hint">Audience: <strong>{faq.role_hint}</strong></div>
                <p class="faq-answer-text">{faq.answer}</p>
              </div>
            {/if}
          </div>
        {/each}
      </div>
    </div>

    <!-- Bottom Quick Launch Cards -->
    <div class="bottom-launch-grid no-print">
      <div class="launch-card" on:click={() => f7router.navigate("/dealer-terminal/")}>
        <div class="card-icon" style="background: rgba(16, 185, 129, 0.15); color: #34d399;">⚡</div>
        <div class="card-info">
          <h4>Live Dealer Terminal</h4>
          <p>Manage 1,000+ UCCs with global kill switches, instant square-offs, and real-time MTM.</p>
        </div>
        <Button fill small color="green" onClick={() => f7router.navigate("/dealer-terminal/")} style="margin-left: auto; border-radius: 6px; font-weight: 700;">
          Launch →
        </Button>
      </div>

      <div class="launch-card" on:click={() => f7router.navigate("/strategy-manager/")}>
        <div class="card-icon" style="background: rgba(59, 130, 246, 0.15); color: #60a5fa;">📊</div>
        <div class="card-info">
          <h4>Strategy & Risk Manager</h4>
          <p>1-Click kill switches, active strategy weights, dynamic multipliers, and live signal feed.</p>
        </div>
        <Button fill small color="blue" onClick={() => f7router.navigate("/strategy-manager/")} style="margin-left: auto; border-radius: 6px; font-weight: 700;">
          Launch →
        </Button>
      </div>

      <div class="launch-card" on:click={() => f7router.navigate("/master-data-manager/")}>
        <div class="card-icon" style="background: rgba(249, 115, 22, 0.15); color: #fb923c;">📁</div>
        <div class="card-info">
          <h4>Master Data & Scrip Tiers</h4>
          <p>Exchange security token lookup, tick sizes, High/Med/Low tier lots, and instant hot-reload sync.</p>
        </div>
        <Button fill small color="orange" onClick={() => f7router.navigate("/master-data-manager/")} style="margin-left: auto; border-radius: 6px; font-weight: 700;">
          Launch →
        </Button>
      </div>
    </div>
  </div>
</Page>

<style>
  :global(.f1f2-guide-page) {
    background-color: #0b0f19 !important;
    color: #e2e8f0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }

  .page-content-wrapper {
    max-width: 1400px;
    margin: 0 auto;
    padding: 16px;
  }

  /* View Mode Switcher in Navbar */
  .view-mode-toggle {
    display: inline-flex;
    background: #1e293b;
    border-radius: 8px;
    padding: 3px;
    border: 1px solid #334155;
  }

  .mode-btn {
    background: transparent;
    border: none;
    color: #94a3b8;
    padding: 6px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .mode-btn.active {
    background: #f97316;
    color: #ffffff;
    box-shadow: 0 2px 6px rgba(249, 115, 22, 0.4);
  }

  /* Executive Hero Card */
  .executive-hero-card {
    background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%);
    border: 1px solid #334155;
    border-radius: 14px;
    padding: 24px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5);
    margin-bottom: 24px;
  }

  .hero-top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
  }

  .badge-group {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .badge-pill {
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 9999px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .badge-autopilot {
    background: #ea580c;
    color: #ffffff;
  }

  .badge-version {
    background: rgba(255, 255, 255, 0.12);
    color: #cbd5e1;
  }

  .badge-contrast {
    background: #2563eb;
    color: #ffffff;
  }

  .badge-engine {
    background: #059669;
    color: #ffffff;
  }

  .quick-nav-links {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .nav-chip {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #f1f5f9;
    padding: 5px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .nav-chip:hover {
    background: rgba(249, 115, 22, 0.2);
    border-color: #f97316;
    color: #fb923c;
  }

  .hero-title {
    margin: 0 0 8px 0;
    font-size: 26px;
    font-weight: 800;
    color: #ffffff;
    letter-spacing: -0.02em;
  }

  .hero-subtitle {
    margin: 0 0 12px 0;
    font-size: 15px;
    color: #fb923c;
    font-weight: 600;
  }

  .hero-desc {
    margin: 0 0 24px 0;
    font-size: 14px;
    color: #cbd5e1;
    line-height: 1.6;
    max-width: 1000px;
  }

  /* Metrics Grid */
  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding-top: 20px;
  }

  .metric-card {
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid #334155;
    border-radius: 10px;
    padding: 14px 18px;
    transition: transform 0.2s ease;
  }

  .metric-card:hover {
    transform: translateY(-2px);
    border-color: #64748b;
  }

  .metric-header {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 6px;
  }

  .metric-badge {
    font-size: 10px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 4px;
    text-transform: uppercase;
  }

  .metric-badge.blue {
    background: rgba(37, 99, 235, 0.2);
    color: #60a5fa;
    border: 1px solid rgba(37, 99, 235, 0.4);
  }

  .metric-badge.green {
    background: rgba(16, 185, 129, 0.2);
    color: #34d399;
    border: 1px solid rgba(16, 185, 129, 0.4);
  }

  .metric-badge.purple {
    background: rgba(168, 85, 247, 0.2);
    color: #c084fc;
    border: 1px solid rgba(168, 85, 247, 0.4);
  }

  .metric-badge.teal {
    background: rgba(20, 184, 166, 0.2);
    color: #2dd4bf;
    border: 1px solid rgba(20, 184, 166, 0.4);
  }

  .metric-value {
    font-size: 24px;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 4px;
  }

  .metric-label {
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .metric-subtext {
    font-size: 11px;
    color: #64748b;
    margin-top: 4px;
    line-height: 1.4;
  }

  /* Contrast Section */
  .contrast-section {
    background: #0f172a;
    border: 1px solid #1e293b;
    border-radius: 14px;
    padding: 24px;
    margin-bottom: 24px;
  }

  .section-tag {
    font-size: 11px;
    font-weight: 700;
    color: #f97316;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    display: inline-block;
    margin-bottom: 6px;
  }

  .section-title {
    font-size: 20px;
    font-weight: 700;
    color: #ffffff;
    margin: 0 0 6px 0;
  }

  .section-desc {
    font-size: 13px;
    color: #94a3b8;
    margin: 0 0 18px 0;
    line-height: 1.5;
  }

  .inline-code {
    background: #1e293b;
    color: #f97316;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 12px;
  }

  .code-contrast-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 16px;
  }

  .contrast-code-card {
    background: #080c14;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .legacy-card {
    border: 1px solid rgba(239, 68, 68, 0.4);
  }

  .modern-card {
    border: 1px solid rgba(16, 185, 129, 0.4);
  }

  .code-card-header {
    background: #111827;
    padding: 10px 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #1f2937;
  }

  .code-title-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .danger-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ef4444;
  }

  .success-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #10b981;
  }

  .code-title-text {
    font-size: 12px;
    font-weight: 700;
    color: #e5e7eb;
    letter-spacing: 0.02em;
  }

  .code-badge {
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .danger-badge {
    background: rgba(239, 68, 68, 0.2);
    color: #f87171;
  }

  .success-badge {
    background: rgba(16, 185, 129, 0.2);
    color: #34d399;
  }

  .copy-btn {
    background: #1e293b;
    border: 1px solid #334155;
    color: #cbd5e1;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 4px;
    cursor: pointer;
  }

  .copy-btn:hover {
    background: #334155;
    color: #ffffff;
  }

  .code-block-container {
    padding: 14px;
    background: #050811;
    overflow-x: auto;
    flex-grow: 1;
    max-height: 280px;
  }

  .code-pre {
    margin: 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 11.5px;
    line-height: 1.45;
    color: #cbd5e1;
  }

  .code-card-footer {
    padding: 10px 14px;
    background: #0d1322;
    display: flex;
    align-items: center;
    gap: 8px;
    border-top: 1px solid #1f2937;
    font-size: 12px;
    color: #94a3b8;
  }

  /* Controls Bar */
  .controls-bar {
    background: #0f172a;
    border: 1px solid #1e293b;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 24px;
  }

  .category-pills {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 10px;
    margin-bottom: 12px;
  }

  .cat-pill {
    background: #1e293b;
    border: 1px solid #334155;
    color: #94a3b8;
    padding: 6px 14px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .cat-pill:hover {
    background: #334155;
    color: #f8fafc;
  }

  .cat-pill.active {
    background: #f97316;
    border-color: #f97316;
    color: #ffffff;
  }

  .search-and-switch {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  .search-box {
    position: relative;
    flex-grow: 1;
    max-width: 480px;
  }

  .search-box input {
    width: 100%;
    background: #080c14;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 8px 32px 8px 12px;
    color: #ffffff;
    font-size: 13px;
    box-sizing: border-box;
  }

  .search-box input:focus {
    outline: none;
    border-color: #f97316;
  }

  .clear-search {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: #64748b;
    cursor: pointer;
    font-size: 14px;
  }

  .view-indicator {
    font-size: 12px;
    color: #94a3b8;
  }

  .view-indicator strong {
    color: #fb923c;
  }

  /* Transformations Cards */
  .transformations-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 30px;
  }

  .transformation-card {
    background: #0f172a;
    border: 1px solid #1e293b;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }

  .card-header-banner {
    background: #131c31;
    padding: 12px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #1e293b;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .trans-num {
    background: #f97316;
    color: #ffffff;
    font-size: 11px;
    font-weight: 800;
    padding: 3px 8px;
    border-radius: 4px;
    letter-spacing: 0.05em;
  }

  .trans-cat {
    font-size: 12px;
    color: #94a3b8;
    font-weight: 600;
  }

  .launch-btn {
    background: rgba(249, 115, 22, 0.15);
    border: 1px solid rgba(249, 115, 22, 0.4);
    color: #fb923c;
    font-size: 12px;
    font-weight: 700;
    padding: 5px 12px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .launch-btn:hover {
    background: #f97316;
    color: #ffffff;
  }

  .card-content-body {
    padding: 20px;
  }

  .trans-title {
    margin: 0 0 6px 0;
    font-size: 19px;
    font-weight: 700;
    color: #ffffff;
  }

  .trans-tagline {
    margin: 0 0 16px 0;
    font-size: 13.5px;
    color: #94a3b8;
    line-height: 1.4;
  }

  /* Mode Boxes */
  .mode-box {
    border-radius: 8px;
    padding: 14px;
    margin-bottom: 18px;
  }

  .dealer-mode-box {
    background: rgba(37, 99, 235, 0.08);
    border: 1px solid rgba(37, 99, 235, 0.25);
  }

  .tech-mode-box {
    background: rgba(16, 185, 129, 0.08);
    border: 1px solid rgba(16, 185, 129, 0.25);
  }

  .mode-box-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }

  .mode-box-badge {
    font-size: 11px;
    font-weight: 800;
    color: #60a5fa;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .mode-box-badge.tech-badge {
    color: #34d399;
  }

  .mode-box-sub {
    font-size: 11px;
    color: #64748b;
  }

  .mode-box-text {
    margin: 0;
    font-size: 13px;
    color: #e2e8f0;
    line-height: 1.5;
  }

  /* Comparison Grid */
  .comparison-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
    gap: 16px;
  }

  .side-column {
    border-radius: 10px;
    padding: 16px;
    display: flex;
    flex-direction: column;
  }

  .legacy-column {
    background: rgba(239, 68, 68, 0.04);
    border: 1px solid rgba(239, 68, 68, 0.25);
  }

  .modern-column {
    background: rgba(16, 185, 129, 0.04);
    border: 1px solid rgba(16, 185, 129, 0.25);
  }

  .column-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .col-title-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .status-pill {
    font-size: 10px;
    font-weight: 800;
    padding: 2px 7px;
    border-radius: 4px;
  }

  .pill-danger {
    background: #ef4444;
    color: #ffffff;
  }

  .pill-success {
    background: #10b981;
    color: #ffffff;
  }

  .col-speed {
    font-size: 11px;
    color: #94a3b8;
    font-family: monospace;
  }

  .risk-badge {
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .risk-critical {
    background: rgba(239, 68, 68, 0.2);
    color: #f87171;
  }

  .risk-zero {
    background: rgba(16, 185, 129, 0.2);
    color: #34d399;
  }

  .col-headline {
    margin: 0 0 6px 0;
    font-size: 14.5px;
    font-weight: 700;
    color: #ffffff;
  }

  .col-desc {
    margin: 0 0 14px 0;
    font-size: 12.5px;
    color: #94a3b8;
    line-height: 1.5;
  }

  .pain-points-list,
  .benefits-list {
    margin-bottom: 14px;
  }

  .list-title {
    font-size: 11px;
    font-weight: 700;
    color: #cbd5e1;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 6px;
  }

  ul {
    margin: 0;
    padding-left: 18px;
    font-size: 12px;
    line-height: 1.5;
  }

  .legacy-column ul li {
    color: #fca5a5;
  }

  .modern-column ul li {
    color: #86efac;
  }

  .snippet-wrapper {
    background: #050811;
    border: 1px solid #1f2937;
    border-radius: 6px;
    overflow: hidden;
    margin-top: auto;
  }

  .snippet-header {
    background: #0d1322;
    padding: 6px 10px;
    font-size: 10px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #1f2937;
  }

  .copy-btn-sm {
    background: #1e293b;
    border: none;
    color: #94a3b8;
    padding: 1px 6px;
    border-radius: 3px;
    font-size: 10px;
    cursor: pointer;
  }

  .snippet-code {
    margin: 0;
    padding: 10px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 11px;
    line-height: 1.4;
    color: #cbd5e1;
    overflow-x: auto;
    max-height: 160px;
  }

  .tech-specs-bar {
    margin-top: 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }

  .specs-label {
    font-size: 10px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
  }

  .spec-tag {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #cbd5e1;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .col-action-footer {
    margin-top: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .direct-action-btn {
    background: #059669;
    border: none;
    color: #ffffff;
    font-size: 11.5px;
    font-weight: 700;
    padding: 6px 14px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .direct-action-btn:hover {
    background: #10b981;
  }

  .sound-test-bar {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .sound-btn {
    background: #1e293b;
    border: 1px solid #334155;
    color: #cbd5e1;
    font-size: 10.5px;
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .sound-btn:hover {
    background: #334155;
    color: #ffffff;
  }

  /* FAQ Accordion Section */
  .faq-section {
    background: #0f172a;
    border: 1px solid #1e293b;
    border-radius: 14px;
    padding: 24px;
    margin-bottom: 24px;
  }

  .faq-accordion-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .faq-item {
    background: #0b0f19;
    border: 1px solid #1e293b;
    border-radius: 8px;
    overflow: hidden;
  }

  .faq-item.active {
    border-color: #f97316;
  }

  .faq-question-btn {
    width: 100%;
    background: transparent;
    border: none;
    padding: 14px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    text-align: left;
    color: #ffffff;
  }

  .faq-q-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .faq-badge {
    background: #1e293b;
    color: #f97316;
    font-size: 10px;
    font-weight: 800;
    padding: 2px 7px;
    border-radius: 4px;
    text-transform: uppercase;
  }

  .faq-question-text {
    font-size: 14px;
    font-weight: 600;
    color: #f8fafc;
  }

  .faq-chevron {
    color: #64748b;
    font-size: 11px;
  }

  .faq-answer-panel {
    padding: 0 16px 14px 16px;
    border-top: 1px solid #1e293b;
    background: rgba(15, 23, 42, 0.4);
  }

  .faq-role-hint {
    font-size: 11px;
    color: #64748b;
    padding-top: 10px;
    margin-bottom: 6px;
  }

  .faq-role-hint strong {
    color: #38bdf8;
  }

  .faq-answer-text {
    margin: 0;
    font-size: 13px;
    color: #cbd5e1;
    line-height: 1.6;
  }

  /* Bottom Launch Cards */
  .bottom-launch-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
    margin-bottom: 30px;
  }

  .launch-card {
    background: #0f172a;
    border: 1px solid #1e293b;
    border-radius: 12px;
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .launch-card:hover {
    background: #131c31;
    border-color: #f97316;
    transform: translateY(-2px);
  }

  .card-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }

  .card-info h4 {
    margin: 0 0 4px 0;
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
  }

  .card-info p {
    margin: 0;
    font-size: 11.5px;
    color: #94a3b8;
    line-height: 1.4;
  }

  .arrow-indicator {
    color: #64748b;
    font-size: 18px;
    margin-left: auto;
  }

  .empty-state {
    text-align: center;
    padding: 40px;
    background: #0f172a;
    border-radius: 12px;
    border: 1px dashed #334155;
  }

  .empty-state h3 {
    margin: 0 0 6px 0;
    color: #ffffff;
  }

  .empty-state p {
    margin: 0 0 16px 0;
    color: #94a3b8;
  }

  /* Print Styles */
  .only-print {
    display: none;
  }

  @media print {
    .no-print {
      display: none !important;
    }

    .only-print {
      display: block !important;
    }

    :global(body),
    :global(.f1f2-guide-page) {
      background: #ffffff !important;
      color: #000000 !important;
    }

    .executive-hero-card,
    .contrast-section,
    .transformation-card,
    .faq-section {
      background: #ffffff !important;
      border: 1px solid #cbd5e1 !important;
      box-shadow: none !important;
      color: #000000 !important;
      page-break-inside: avoid;
      margin-bottom: 16px;
    }

    .hero-title,
    .section-title,
    .trans-title,
    .col-headline,
    .faq-question-text {
      color: #000000 !important;
    }

    .hero-subtitle,
    .section-desc,
    .trans-tagline,
    .col-desc,
    .faq-answer-text {
      color: #334155 !important;
    }

    .contrast-code-card,
    .snippet-wrapper {
      background: #f8fafc !important;
      border: 1px solid #e2e8f0 !important;
    }

    .code-pre code,
    .snippet-code code {
      color: #0f172a !important;
    }

    .legacy-column {
      background: #fff1f2 !important;
      border: 1px solid #fecdd3 !important;
    }

    .modern-column {
      background: #f0fdf4 !important;
      border: 1px solid #bbf7d0 !important;
    }

    .legacy-column ul li {
      color: #991b1b !important;
    }

    .modern-column ul li {
      color: #166534 !important;
    }

    .metric-card {
      background: #f8fafc !important;
      border: 1px solid #cbd5e1 !important;
    }

    .metric-value {
      color: #000000 !important;
    }

    .dealer-mode-box,
    .tech-mode-box {
      background: #f1f5f9 !important;
      border: 1px solid #cbd5e1 !important;
      color: #000000 !important;
    }

    .mode-box-text {
      color: #0f172a !important;
    }
  }

  @media (max-width: 768px) {
    .hero-top-row {
      flex-direction: column;
      align-items: flex-start;
    }

    .code-contrast-grid,
    .comparison-grid {
      grid-template-columns: 1fr;
    }

    .search-box {
      max-width: 100%;
    }
  }
</style>
